import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { SkeletonLoader } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { OverviewEventFeed } from '~/modules/overview-event-feed/OverviewEventFeed';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';

import styles from './EntityFullViewDrawer.module.scss';

const EVENTS_CONFIG = { TMI: true };

export interface EntityFullViewDrawerProps {
  trackingSource: PosthogTrackingSource;
  entity?: Entity;
  isEntityLoading?: boolean;
  isEntityPending?: boolean;
  isError?: boolean;
}

export const EntityFullViewDrawer = ({
  trackingSource,
  entity,
  isEntityLoading,
  isEntityPending,
  isError,
}: EntityFullViewDrawerProps) => {
  const showSkeleton = Boolean(!entity && (isEntityLoading || isEntityPending));

  if (showSkeleton) {
    return (
      <SkeletonLoader
        className={styles.container}
        areas={[
          {
            styles: {
              width: '100%',
              height: '296px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '600px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!entity || isError) {
    return <ErrorMessageWrapper variant="empty" className={styles.container} />;
  }

  const chartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneHour,
    horizon: RangeHorizon.OneMonth,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  return (
    <div className={styles.container}>
      <ErrorBoundaryWrapper>
        <SimpleChart
          entity={entity}
          source={trackingSource}
          initialChartSettings={chartSettings}
          showChartTypeSelector={false}
          eventsConfig={EVENTS_CONFIG}
        />
        <OverviewEventFeed
          entityTag={entity.tag}
          period="1M"
          isDrawer
          showViewAllButton
          source={trackingSource}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
